import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, ChevronDown, ListPlus, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  FlowEtfMetricsDto,
  FlowGroupDto,
  FlowLeadershipPointDto,
  FlowPeriod,
  FlowReturnsDto,
  MarketFlowDto,
  ScanWindow,
} from 'shared';

import { HorizonSelector, type HorizonOption } from '../components/ui/HorizonSelector';
import { useEtfHoldings, useMarketFlow } from '../features/marketConsole/queries';
import { compactDollars, formatSignedPct, SCAN_HORIZON_OPTIONS } from '../lib/marketConsoleFormat';
import { useReplayDate } from '../lib/useReplayDate';
import { cn } from '../lib/utils';

const UP_COLOR = '#10B981';
const DOWN_COLOR = '#EF4444';
const CHART_ROWS = 15;
const SECTOR_COLORS: Record<string, string> = {
  'Communication services': '#60A5FA',
  'Consumer discretionary': '#F472B6',
  'Consumer staples': '#A3E635',
  Energy: '#F59E0B',
  Financials: '#34D399',
  'Health care': '#22D3EE',
  Industrials: '#C084FC',
  Materials: '#FB923C',
  'Real estate': '#FACC15',
  Technology: '#818CF8',
  Utilities: '#94A3B8',
};
const FALLBACK_COLORS = ['#60A5FA', '#F472B6', '#A3E635'];

const PERIOD_OPTIONS: ReadonlyArray<HorizonOption<FlowPeriod>> = [
  { value: 'PREMARKET', label: 'Pre' },
  { value: 'AFTER_HOURS', label: 'After hrs' },
  { value: 'INTRADAY', label: 'Intraday' },
  { value: '1D', label: '1D' },
  { value: '1W', label: '1W' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: '1Y', label: '1Y' },
];

type ReturnsView = 'groups' | 'etfs';
const VIEW_OPTIONS: ReadonlyArray<HorizonOption<ReturnsView>> = [
  { value: 'groups', label: 'Themes' },
  { value: 'etfs', label: 'ETFs' },
];

function pctTone(value: number | null): string {
  if (value === null || value === 0) return 'text-text-muted';
  return value > 0 ? 'text-success' : 'text-danger';
}

function formatOptionalPct(value: number | null): string {
  return value === null ? '—' : formatSignedPct(value);
}

function sectorColor(group: string, index: number): string {
  return SECTOR_COLORS[group] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length] ?? '#60A5FA';
}

function HoldingsDialog({ symbol, onClose }: { symbol: string | null; onClose: () => void }) {
  const holdings = useEtfHoldings(symbol);
  return (
    <Dialog.Root
      open={symbol !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-lg border border-border bg-bg p-4 shadow-xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-[560px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-semibold">{symbol} top holdings</Dialog.Title>
              <Dialog.Description className="text-xs text-text-muted">
                {holdings.data?.asOf
                  ? `Holdings retrieved ${holdings.data.asOf}; liquidity and extension as of ${holdings.data.statsAsOf ?? '—'}.`
                  : 'Stocks only; cash, bonds, derivatives and funds are excluded.'}
              </Dialog.Description>
            </div>
            <Dialog.Close
              className="rounded p-1 text-text-muted hover:bg-surface-raised hover:text-text"
              aria-label="Close holdings"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          {holdings.isLoading ? (
            <p className="mt-4 text-sm text-text-muted">Loading holdings…</p>
          ) : holdings.isError ? (
            <p className="mt-4 text-sm text-danger">Holdings could not be loaded.</p>
          ) : holdings.data && holdings.data.holdings.length > 0 ? (
            <table className="mt-4 w-full text-sm tabular-nums">
              <thead className="text-xs text-text-muted">
                <tr className="border-b border-border">
                  <th scope="col" className="py-2 text-left font-medium">
                    Stock
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    Weight
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    $ vol 30D
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    ATRs over 50D
                  </th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {holdings.data.holdings.map((holding) => (
                  <tr key={holding.symbol} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-2 font-sans">
                      <div className="font-semibold">{holding.symbol}</div>
                      <div className="truncate text-[11px] text-text-muted">{holding.name}</div>
                    </td>
                    <td className="py-1.5 text-right">{holding.weightPct.toFixed(2)}%</td>
                    <td className="py-1.5 text-right">
                      {holding.averageDollarVolume30d === null
                        ? '—'
                        : compactDollars.format(holding.averageDollarVolume30d)}
                    </td>
                    <td className="py-1.5 text-right">
                      {holding.atrExtension === null ? '—' : `${holding.atrExtension.toFixed(1)}×`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="mt-4 text-sm text-text-muted">
              No stock holdings are stored for {symbol}. Commodity and crypto funds hold no stocks.
            </p>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function MemberRow({
  member,
  onHoldings,
}: {
  member: FlowEtfMetricsDto;
  onHoldings: (symbol: string) => void;
}) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 py-2 text-sm">
      <button
        type="button"
        onClick={() => onHoldings(member.symbol)}
        className="min-w-0 text-left"
        aria-label={`Show ${member.symbol} holdings`}
      >
        <span className="font-semibold underline decoration-dotted underline-offset-2">
          {member.symbol}
        </span>
        <span className="ml-2 truncate text-[11px] text-text-muted">{member.description}</span>
      </button>
      <span className={cn('text-right font-mono tabular-nums', pctTone(member.performance))}>
        {formatSignedPct(member.performance)}
      </span>
      <div className="col-span-2 flex flex-wrap gap-x-3 font-mono text-[11px] tabular-nums text-text-muted">
        <span>pct {member.percentile.toFixed(0)}</span>
        <span className={pctTone(member.oneDayChangePct)}>
          1D {formatOptionalPct(member.oneDayChangePct)}
        </span>
        <span>ADR {member.adrPct === null ? '—' : `${member.adrPct.toFixed(1)}%`}</span>
        <span className={member.nonExtended ? undefined : 'text-warn'}>
          {member.atrExtension.toFixed(1)}× ATR over 50D
          {member.nonExtended ? '' : ' (extended)'}
        </span>
        <span>
          {member.averageDollarVolume30d === null
            ? '—'
            : `${compactDollars.format(member.averageDollarVolume30d)}/day`}
        </span>
        {member.isTight ? (
          <span className="text-success">Tight {member.coilSetup ?? ''}</span>
        ) : null}
      </div>
    </li>
  );
}

function GroupRow({
  group,
  maxMedian,
  onHoldings,
}: {
  group: FlowGroupDto;
  maxMedian: number;
  onHoldings: (symbol: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const width =
    maxMedian > 0 ? Math.max(4, (Math.max(group.medianPerformance, 0) / maxMedian) * 100) : 4;
  return (
    <li className="py-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="grid w-full grid-cols-[1.5rem_minmax(0,1fr)_auto_1rem] items-center gap-2 text-left"
      >
        <span className="font-mono text-xs text-text-muted">{group.rank}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">{group.group}</span>
          <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-bg-muted">
            <span
              className={cn(
                'block h-full rounded-full',
                group.medianPerformance >= 0 ? 'bg-success' : 'bg-danger',
              )}
              style={{ width: `${width}%` }}
            />
          </span>
        </span>
        <span className={cn('font-mono text-sm tabular-nums', pctTone(group.medianPerformance))}>
          {formatSignedPct(group.medianPerformance)}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-text-muted transition', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      <div className="ml-8 mt-1 flex flex-wrap gap-x-3 text-[11px] text-text-muted">
        <span>Score {group.score.toFixed(0)}</span>
        <span>
          {group.confirmation}/{group.memberCount} ETFs in top 20%
        </span>
        <span>
          {group.nonExtendedMembers}/{group.memberCount} non-extended
        </span>
        <span>Leader {group.leaderEtf}</span>
      </div>
      {open ? (
        <ul className="ml-8 mt-1 divide-y divide-border border-l border-border pl-3">
          {group.members.map((member) => (
            <MemberRow key={member.symbol} member={member} onHoldings={onHoldings} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function GroupList({
  groups,
  emptyText,
  onHoldings,
}: {
  groups: readonly FlowGroupDto[];
  emptyText: string;
  onHoldings: (symbol: string) => void;
}) {
  if (groups.length === 0) return <p className="py-4 text-sm text-text-muted">{emptyText}</p>;
  const maxMedian = Math.max(...groups.map((group) => group.medianPerformance), 0);
  return (
    <ol className="divide-y divide-border">
      {groups.map((group) => (
        <GroupRow key={group.group} group={group} maxMedian={maxMedian} onHoldings={onHoldings} />
      ))}
    </ol>
  );
}

function LeadershipChart({
  points,
  sectors,
}: {
  points: readonly FlowLeadershipPointDto[];
  sectors: readonly string[];
}) {
  const data = points.map((point) => ({ date: point.date, ...point.scores }));
  return (
    <div
      className="h-56"
      role="img"
      aria-label={`Leadership score over the last ${points.length} sessions for ${sectors.join(', ')}`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
          <YAxis tick={{ fontSize: 11 }} width={32} domain={[0, 100]} />
          <Tooltip />
          {sectors.map((sector, index) => (
            <Line
              key={sector}
              type="monotone"
              dataKey={sector}
              name={sector}
              stroke={sectorColor(sector, index)}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ReturnsChart({ returns }: { returns: FlowReturnsDto }) {
  const [period, setPeriod] = useState<FlowPeriod>('1D');
  const [view, setView] = useState<ReturnsView>('groups');
  const rows = (view === 'groups' ? returns.groups : returns.etfs)
    .flatMap((row) => {
      const value = row.values[period];
      return value === null ? [] : [{ key: row.key, value }];
    })
    .sort((left, right) => right.value - left.value)
    .slice(0, CHART_ROWS);
  const capturedAt =
    period === 'PREMARKET' || period === 'AFTER_HOURS' ? returns.extendedCapturedAt[period] : null;
  return (
    <section
      className="rounded-lg border border-border bg-surface p-4"
      aria-labelledby="flow-returns-heading"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 id="flow-returns-heading" className="text-sm font-semibold">
            Theme performance
          </h2>
          <p className="mt-1 text-xs text-text-muted">
            {view === 'groups' ? 'Median return per theme' : 'Return per ETF'}; top {CHART_ROWS}.
            {capturedAt ? ` Captured ${new Date(capturedAt).toLocaleString()}.` : ''}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <HorizonSelector
            label="Return period"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
          />
          <HorizonSelector
            label="Return grouping"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-sm text-text-muted">
          No values for this period yet. Extended-hours values appear after their session is
          captured.
        </p>
      ) : (
        <div
          className="mt-3"
          style={{ height: rows.length * 26 + 24 }}
          role="img"
          aria-label="Theme performance chart"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
            >
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="key" width={130} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => (typeof value === 'number' ? formatSignedPct(value) : value)}
              />
              <ReferenceLine x={0} stroke="#6B7280" />
              <Bar dataKey="value" isAnimationActive={false}>
                {rows.map((row) => (
                  <Cell key={row.key} fill={row.value >= 0 ? UP_COLOR : DOWN_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function IndustryLeadership({
  data,
  horizon,
}: {
  data: NonNullable<MarketFlowDto['industryLeadership']>;
  horizon: ScanWindow;
}) {
  const rows = data.windows[horizon];
  return (
    <section
      className="rounded-lg border border-border bg-surface p-4"
      aria-labelledby="flow-industry-heading"
    >
      <h2 id="flow-industry-heading" className="text-sm font-semibold">
        Industry leadership · {horizon}
      </h2>
      <p className="mt-1 text-xs text-text-muted">
        Industries with the most momentum leaders in the stock scan of {data.sessionDate}, extended
        or not.
      </p>
      {rows.length === 0 ? (
        <p className="py-4 text-sm text-text-muted">No leaders in this horizon.</p>
      ) : (
        <ol className="mt-2 divide-y divide-border text-sm">
          {rows.map((row) => (
            <li key={row.industry} className="flex justify-between gap-3 py-1.5">
              <span className="min-w-0 truncate">{row.industry}</span>
              <span className="font-mono tabular-nums text-text-muted">{row.leaders}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function MarketFlow() {
  const flow = useMarketFlow(useReplayDate().asOf);
  const [horizon, setHorizon] = useState<ScanWindow>('1M');
  const [holdingsSymbol, setHoldingsSymbol] = useState<string | null>(null);

  if (flow.isLoading) {
    return <main className="p-6 text-text-muted">Loading flow…</main>;
  }
  if (flow.isError || !flow.data) {
    return <main className="p-6 text-danger">Flow data could not be loaded.</main>;
  }

  const data = flow.data;
  const sectors = data.sectors?.windows[horizon] ?? [];
  const leadershipSectors = sectors.map((sector) => sector.group);

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Flow</h1>
            <p className="mt-1 text-sm text-text-muted">
              Where money is moving: theme and sector ETF leadership across horizons.
            </p>
          </div>
          <div className="flex flex-col items-start gap-1 text-xs text-text-muted sm:items-end">
            {data.sessionDate ? <span>Close · {data.sessionDate} · Informational only</span> : null}
            <Link to="/settings/flow-etfs" className="inline-flex items-center gap-1 underline">
              <ListPlus className="h-3.5 w-3.5" aria-hidden="true" />
              Edit ETF lists
            </Link>
          </div>
        </header>

        {data.status === 'NOT_AVAILABLE' ? (
          <section className="flex gap-3 rounded-lg border border-warn/30 bg-warn/10 p-4 text-sm text-warn">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              No ETF data yet. Run the ETF flow refresh in{' '}
              <Link to="/settings/market-data" className="underline">
                Settings → Market data
              </Link>
              .
            </p>
          </section>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                Ranking horizon
              </span>
              <HorizonSelector
                label="Ranking horizon"
                options={SCAN_HORIZON_OPTIONS}
                value={horizon}
                onChange={setHorizon}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <section
                className="rounded-lg border border-border bg-surface p-4"
                aria-labelledby="flow-themes-heading"
              >
                <h2 id="flow-themes-heading" className="text-sm font-semibold">
                  Leading themes · {horizon}
                </h2>
                <p className="mt-1 text-xs text-text-muted">
                  Score = 70% median ETF percentile + 30% best ETF percentile. Open a theme to see
                  its ETFs; tap an ETF for its top holdings.
                </p>
                <GroupList
                  groups={data.themes?.windows[horizon] ?? []}
                  emptyText="No ranked themes for this horizon."
                  onHoldings={setHoldingsSymbol}
                />
              </section>

              <section
                className="rounded-lg border border-border bg-surface p-4"
                aria-labelledby="flow-sectors-heading"
              >
                <h2 id="flow-sectors-heading" className="text-sm font-semibold">
                  Leading sectors · {horizon}
                </h2>
                <GroupList
                  groups={sectors}
                  emptyText="No ranked sectors for this horizon."
                  onHoldings={setHoldingsSymbol}
                />
                {data.sectorLeadership && leadershipSectors.length > 0 ? (
                  <>
                    <h3 className="mt-4 text-xs font-semibold text-text-muted">
                      Leadership score, last {data.sectorLeadership[horizon].length} sessions
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {leadershipSectors.map((sector, index) => (
                        <span
                          key={sector}
                          className="inline-flex items-center gap-1.5 rounded border border-border px-2 py-0.5 text-xs text-text-muted"
                        >
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{ backgroundColor: sectorColor(sector, index) }}
                            aria-hidden="true"
                          />
                          {sector}
                        </span>
                      ))}
                    </div>
                    <LeadershipChart
                      points={data.sectorLeadership[horizon]}
                      sectors={leadershipSectors}
                    />
                  </>
                ) : null}
              </section>
            </div>

            {data.themeReturns ? <ReturnsChart returns={data.themeReturns} /> : null}

            {data.pendingSymbols.length > 0 ? (
              <p className="text-xs text-warn">
                Awaiting data for: {data.pendingSymbols.join(', ')}. They join the ranking after the
                next ETF flow refresh.
              </p>
            ) : null}
          </>
        )}

        {data.industryLeadership ? (
          <IndustryLeadership data={data.industryLeadership} horizon={horizon} />
        ) : null}

        <p className="text-xs text-text-muted">
          Data:{' '}
          <a href={data.source.url} target="_blank" rel="noopener noreferrer" className="underline">
            {data.source.name}
          </a>
          . Leadership shows where performance has been, not where it will go.
        </p>
      </div>
      <HoldingsDialog symbol={holdingsSymbol} onClose={() => setHoldingsSymbol(null)} />
    </main>
  );
}
